class SSEManager {
  constructor(baseUrl = "https://sse.lively.li") {
    this.baseUrl = baseUrl;
    this.connections = new Map(); // channelId -> EventSource
    this.listeners = new Map(); // channelId -> Map<listenerId -> callback>
    this.reconnectAttempts = new Map(); // channelId -> attempt count
    this.reconnectTimers = new Map(); // channelId -> timeoutId

    // Configuration
    this.config = {
      maxReconnectAttempts: 10,
      initialReconnectDelay: 1000, // 1 second
      maxReconnectDelay: 30000, // 30 seconds
      reconnectBackoffMultiplier: 1.5,
      heartbeatInterval: 30000, // 30 seconds
      connectionTimeout: 60000, // 60 seconds
    };

    // Connection health tracking
    this.lastMessageTime = new Map(); // channelId -> timestamp
    this.healthCheckIntervals = new Map(); // channelId -> intervalId

    console.log("[SSEManager] Initialized");
  }

  subscribe(channelId, onMessage, options = {}) {
    const listenerId = `listener_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    console.log(`[SSEManager] Subscribing to channel: ${channelId}`, {
      listenerId,
    });

    if (!this.listeners.has(channelId)) {
      this.listeners.set(channelId, new Map());
    }

    this.listeners.get(channelId).set(listenerId, {
      callback: onMessage,
      options: options,
      subscribedAt: Date.now(),
    });

    if (!this.connections.has(channelId)) {
      this.connect(channelId);
    }

    return () => this.unsubscribe(channelId, listenerId);
  }

  unsubscribe(channelId, listenerId) {
    console.log(
      `[SSEManager] Unsubscribing listener ${listenerId} from ${channelId}`,
    );

    const channelListeners = this.listeners.get(channelId);
    if (channelListeners) {
      channelListeners.delete(listenerId);

      if (channelListeners.size === 0) {
        this.disconnect(channelId);
      }
    }
  }

  connect(channelId) {
    if (this.connections.has(channelId)) {
      console.log(`[SSEManager] Connection already exists for ${channelId}`);
      return;
    }

    console.log(`[SSEManager] Connecting to channel: ${channelId}`);

    try {
      const url = `${this.baseUrl}/subscribe-stream?channel=${channelId}`;
      const eventSource = new EventSource(url);

      this.connections.set(channelId, eventSource);
      this.lastMessageTime.set(channelId, Date.now());

      eventSource.onopen = () => this.handleOpen(channelId);
      eventSource.onmessage = (event) => this.handleMessage(channelId, event);
      eventSource.onerror = (error) => this.handleError(channelId, error);

      this.startHealthCheck(channelId);

      this.reconnectAttempts.set(channelId, 0);
    } catch (error) {
      console.error(`[SSEManager] Failed to connect to ${channelId}:`, error);
      this.scheduleReconnect(channelId);
    }
  }

  handleOpen(channelId) {
    console.log(`[SSEManager] Connection established: ${channelId}`);
    this.reconnectAttempts.set(channelId, 0);
    this.notifyListeners(channelId, {
      type: "_connection_status",
      status: "connected",
      channel: channelId,
      timestamp: Date.now(),
    });
  }

  handleMessage(channelId, event) {
    this.lastMessageTime.set(channelId, Date.now());

    try {
      const data = event.data ? JSON.parse(event.data) : null;

      console.log(`[SSEManager] Message received on ${channelId}:`, data);

      if (!data) return;

      this.notifyListeners(channelId, data);
    } catch (error) {
      console.error(
        `[SSEManager] Error parsing message from ${channelId}:`,
        error,
      );
    }
  }

  handleError(channelId, error) {
    console.error(`[SSEManager] Connection error on ${channelId}:`, error);

    const connection = this.connections.get(channelId);

    if (connection && connection.readyState === EventSource.CLOSED) {
      console.log(
        `[SSEManager] Connection closed for ${channelId}, attempting reconnect`,
      );
      this.disconnect(channelId, false);
      this.scheduleReconnect(channelId);
    }

    this.notifyListeners(channelId, {
      type: "_connection_status",
      status: "error",
      channel: channelId,
      error: error,
      timestamp: Date.now(),
    });
  }

  notifyListeners(channelId, data) {
    const channelListeners = this.listeners.get(channelId);

    if (!channelListeners || channelListeners.size === 0) {
      return;
    }

    channelListeners.forEach((listener) => {
      try {
        listener.callback(data, channelId);
      } catch (error) {
        console.error(
          `[SSEManager] Error in listener callback for ${channelId}:`,
          error,
        );
      }
    });
  }

  scheduleReconnect(channelId) {
    const channelListeners = this.listeners.get(channelId);
    if (!channelListeners || channelListeners.size === 0) {
      console.log(
        `[SSEManager] No listeners for ${channelId}, skipping reconnect`,
      );
      return;
    }

    const attempts = this.reconnectAttempts.get(channelId) || 0;

    if (attempts >= this.config.maxReconnectAttempts) {
      console.error(
        `[SSEManager] Max reconnect attempts reached for ${channelId}`,
      );
      this.notifyListeners(channelId, {
        type: "_connection_status",
        status: "failed",
        channel: channelId,
        reason: "max_reconnect_attempts_exceeded",
        timestamp: Date.now(),
      });
      return;
    }

    const delay = Math.min(
      this.config.initialReconnectDelay *
        Math.pow(this.config.reconnectBackoffMultiplier, attempts),
      this.config.maxReconnectDelay,
    );

    console.log(
      `[SSEManager] Scheduling reconnect for ${channelId} in ${delay}ms (attempt ${attempts + 1})`,
    );

    const existingTimer = this.reconnectTimers.get(channelId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      this.reconnectAttempts.set(channelId, attempts + 1);
      this.reconnectTimers.delete(channelId);
      this.connect(channelId);
    }, delay);

    this.reconnectTimers.set(channelId, timer);

    this.notifyListeners(channelId, {
      type: "_connection_status",
      status: "reconnecting",
      channel: channelId,
      attempt: attempts + 1,
      delay: delay,
      timestamp: Date.now(),
    });
  }

  startHealthCheck(channelId) {
    this.stopHealthCheck(channelId);

    const intervalId = setInterval(() => {
      const lastMessage = this.lastMessageTime.get(channelId);
      const now = Date.now();

      if (lastMessage && now - lastMessage > this.config.connectionTimeout) {
        console.warn(
          `[SSEManager] Connection timeout for ${channelId}, reconnecting`,
        );
        this.disconnect(channelId, false);
        this.scheduleReconnect(channelId);
      }
    }, this.config.heartbeatInterval);

    this.healthCheckIntervals.set(channelId, intervalId);
  }

  stopHealthCheck(channelId) {
    const intervalId = this.healthCheckIntervals.get(channelId);
    if (intervalId) {
      clearInterval(intervalId);
      this.healthCheckIntervals.delete(channelId);
    }
  }

  disconnect(channelId, removeListeners = true) {
    console.log(`[SSEManager] Disconnecting from ${channelId}`);

    const connection = this.connections.get(channelId);
    if (connection) {
      connection.close();
      this.connections.delete(channelId);
    }

    const timer = this.reconnectTimers.get(channelId);
    if (timer) {
      clearTimeout(timer);
      this.reconnectTimers.delete(channelId);
    }

    this.stopHealthCheck(channelId);

    this.lastMessageTime.delete(channelId);
    this.reconnectAttempts.delete(channelId);

    if (removeListeners) {
      this.listeners.delete(channelId);
    }
  }

  disconnectAll() {
    console.log("[SSEManager] Disconnecting all channels");

    this.connections.forEach((_, channelId) => {
      this.disconnect(channelId, true);
    });

    this.connections.clear();
    this.listeners.clear();
    this.reconnectAttempts.clear();
    this.reconnectTimers.clear();
    this.lastMessageTime.clear();
    this.healthCheckIntervals.clear();
  }

  getStatus(channelId) {
    const connection = this.connections.get(channelId);
    const listeners = this.listeners.get(channelId);
    const lastMessage = this.lastMessageTime.get(channelId);
    const attempts = this.reconnectAttempts.get(channelId) || 0;

    return {
      connected: connection && connection.readyState === EventSource.OPEN,
      readyState: connection ? connection.readyState : null,
      listenerCount: listeners ? listeners.size : 0,
      lastMessageTime: lastMessage,
      reconnectAttempts: attempts,
    };
  }

  getAllStatuses() {
    const statuses = {};
    this.connections.forEach((_, channelId) => {
      statuses[channelId] = this.getStatus(channelId);
    });
    return statuses;
  }
}

const sseManager = new SSEManager();
export default sseManager;
